import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, X, MapPin, Clock, MessageCircle, Copy, AlertCircle, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate, formatINR } from '../lib/format';
import AmPmTimePicker from '../components/AmPmTimePicker';
import { EVENT_TYPES } from '../lib/content';
import { waLink } from '../lib/whatsapp';

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [addEvent, setAddEvent] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [expenses] = useState([]); // Phase 2

  const load = async () => {
    const r = await api.get(`/projects/${id}`); setP(r.data);
    const i = await api.get('/invoices'); setInvoices(i.data.filter(x => x.project_id === id));
    const a = await api.get('/assignments'); setAssignments(a.data.filter(x => x.project_id === id));
  };
  useEffect(() => { load(); }, [id]);

  if (!p) return <div className="p-6 text-sm text-[var(--muted)]">Loading…</div>;

  const saveEvent = async (ev) => {
    const events = [...(p.events||[])];
    if (ev.id) {
      const i = events.findIndex(x => x.id === ev.id);
      const old = events[i];
      events[i] = ev;
      const change = { at: new Date().toISOString(), note: `Edited ${ev.name}: date=${ev.date}, report=${ev.reporting_time}, ready=${ev.ready_by_time}` };
      await api.put(`/projects/${id}`, { events, change_log: [...(p.change_log||[]), change] });
    } else {
      ev.id = crypto.randomUUID();
      events.push(ev);
      const change = { at: new Date().toISOString(), note: `Added event ${ev.name} on ${ev.date}` };
      await api.put(`/projects/${id}`, { events, change_log: [...(p.change_log||[]), change] });
    }
    toast.success('Event saved');
    setAddEvent(false); setEditEvent(null); load();
  };

  const removeEvent = async (evId) => {
    if (!window.confirm('Delete this event?')) return;
    const events = (p.events||[]).filter(e => e.id !== evId);
    await api.put(`/projects/${id}`, { events });
    load();
  };

  // Profit calculations with §16 absorbed vs recharged distinction
  const clientRevenue = invoices.reduce((s, i) => s + (i.total || 0), 0);
  // Absorbed crew cost = fee + allowance for assignments not billed to client
  const absorbedCrewCost = assignments.filter(a => (a.cost_type || 'absorbed') === 'absorbed').reduce((s, a) => s + (a.fee || 0) + (a.allowance || 0), 0);
  // Recharged crew cost = fee + allowance for recharged (client pays for the service via invoice; this is what I pay the artist)
  const rechargedCrewCost = assignments.filter(a => a.cost_type === 'recharged').reduce((s, a) => s + (a.fee || 0) + (a.allowance || 0), 0);
  const rechargedBilled = assignments.filter(a => a.cost_type === 'recharged').reduce((s, a) => s + (a.client_billed || 0), 0);
  const rechargedMargin = rechargedBilled - rechargedCrewCost;
  const travel = (p.events || []).reduce((s, e) => s + (e.travel_lines || []).reduce((t, l) => t + (l.amount||0), 0), 0);
  const crewCostTotal = absorbedCrewCost + rechargedCrewCost;
  const profit = clientRevenue - crewCostTotal - travel;

  return (
    <div className="px-5 py-5 lg:px-10 lg:py-8 max-w-5xl mx-auto">
      <Link to="/projects" className="eyebrow text-[var(--gold)]">← Projects</Link>
      <div className="mb-2 mt-2 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">{p.type}</div>
          <h1 className="serif text-[32px] leading-tight">{p.name}</h1>
          <div className="text-sm text-[var(--muted)] mt-1">{p.client_name} · {p.city}{p.destination ? ' · Destination' : ''}</div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link to={`/quotations/new?project=${id}`} className="btn-ghost" data-testid="proj-create-quotation">New Quotation</Link>
          <Link to={`/invoices/new?project=${id}`} className="btn-gold" data-testid="proj-create-invoice">New Invoice</Link>
        </div>
      </div>

      {/* Profit card */}
      <div className="card-alt p-5 mt-5 grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="proj-profit">
        <div><div className="eyebrow">Client Revenue</div><div className="serif text-[22px] mono-num">{formatINR(clientRevenue)}</div></div>
        <div><div className="eyebrow">Crew Cost</div><div className="serif text-[22px] mono-num">−{formatINR(crewCostTotal)}</div>
          <div className="text-[10px] text-[var(--muted)] mt-0.5">Absorbed {formatINR(absorbedCrewCost)} · Recharged {formatINR(rechargedCrewCost)}</div></div>
        <div><div className="eyebrow">Travel</div><div className="serif text-[22px] mono-num">−{formatINR(travel)}</div></div>
        <div><div className="eyebrow">Net</div><div className="serif text-[26px] mono-num" style={{color: profit>=0?'var(--ok)':'var(--overdue)'}}>{formatINR(profit)}</div>
          {rechargedBilled > 0 && <div className="text-[10px] mt-0.5" style={{color: rechargedMargin>=0?'var(--ok)':'var(--overdue)'}}>Recharged margin {formatINR(rechargedMargin)}</div>}
        </div>
      </div>

      {/* Events */}
      <div className="mt-8 flex items-center justify-between">
        <div className="eyebrow">Events</div>
        <button className="btn-ghost" onClick={() => setAddEvent(true)} data-testid="event-add-btn"><Plus size={14}/> Add Event</button>
      </div>
      <div className="space-y-3 mt-3">
        {(p.events||[]).map(ev => (
          <div key={ev.id} className="card-cream p-4" data-testid={`event-${ev.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="serif text-[19px]">{ev.name}</div>
                  {ev.needs_confirmation && <span className="needs-conf-badge"><AlertCircle size={11}/> Needs confirmation</span>}
                </div>
                <div className="text-sm text-[var(--muted)] mt-1 flex gap-3 flex-wrap">
                  {ev.date && <span>{formatDate(ev.date)}</span>}
                  {ev.reporting_time && <span className="flex items-center gap-1"><Clock size={12}/>Report {ev.reporting_time}</span>}
                  {ev.ready_by_time && <span>Ready {ev.ready_by_time}</span>}
                  {ev.venue_name && <span className="flex items-center gap-1"><MapPin size={12}/>{ev.venue_name}</span>}
                </div>
                {ev.confirmation_note && <div className="mt-2 text-xs" style={{color:'var(--pending)'}}>{ev.confirmation_note}</div>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => setEditEvent(ev)} className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" data-testid={`event-edit-${ev.id}`}>Edit</button>
                <button onClick={() => removeEvent(ev.id)} className="text-xs text-[var(--overdue)] tracking-[0.14em] uppercase">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {(p.events||[]).length === 0 && <div className="text-sm text-[var(--muted)] italic">No events yet. Add mehendi, wedding, reception etc.</div>}
      </div>

      {/* Invoices */}
      <div className="mt-8">
        <div className="eyebrow mb-2">Invoices</div>
        {invoices.length === 0 && <div className="text-sm text-[var(--muted)] italic">No invoices yet.</div>}
        {invoices.map(i => (
          <Link to={`/invoices/${i.id}`} key={i.id} className="card-cream p-3 block mb-2 flex items-center justify-between hover:bg-[var(--cream-alt)]">
            <div><div className="serif">{i.number}</div><div className="text-xs text-[var(--muted)]">{formatDate(i.event_date)} · {i.client_name}</div></div>
            <div className="text-right"><div className="serif mono-num">{formatINR(i.total)}</div><span className={`status-pill status-${i.status==='fully_paid'?'ok':i.status==='overdue'?'overdue':'pending'}`}>{i.status.replace('_',' ')}</span></div>
          </Link>
        ))}
      </div>

      {/* Change log */}
      {(p.change_log||[]).length > 0 && (
        <div className="mt-8">
          <div className="eyebrow mb-2">Change Log</div>
          <div className="card-alt p-4 text-xs space-y-1 text-[var(--muted)]">
            {(p.change_log||[]).slice(-8).reverse().map((c, i) => (
              <div key={i}>{new Date(c.at).toLocaleString('en-GB')} — {c.note}</div>
            ))}
          </div>
        </div>
      )}

      {/* Consultation & consent share links */}
      <div className="mt-8 card-alt p-4">
        <div className="eyebrow mb-2">Client Forms</div>
        <div className="text-xs text-[var(--muted)] mb-2">Share these read-only links with {p.client_name || 'the client'} — responses save back onto this project.</div>
        <div className="space-y-2">
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-xs uppercase tracking-[0.14em] w-32">Consultation</span>
            <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/consultation/${p.id}`); toast.success('Link copied'); }} data-testid="proj-copy-consultation">Copy link</button>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-xs uppercase tracking-[0.14em] w-32">Consent</span>
            <button className="btn-ghost text-xs" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/public/consent/${p.id}`); toast.success('Link copied'); }} data-testid="proj-copy-consent">Copy link</button>
          </div>
        </div>
      </div>

      {(addEvent || editEvent) && <EventForm value={editEvent} onClose={() => { setAddEvent(false); setEditEvent(null); }} onSave={saveEvent} />}
    </div>
  );
}

function EventForm({ value, onClose, onSave }) {
  const [f, setF] = useState({
    name: 'Wedding', date: '', reporting_time: '', ready_by_time: '',
    venue_name: '', address: '', map_link: '',
    needs_confirmation: false, confirmation_note: '',
    services: [], travel_lines: [], notes: '', ...value
  });
  const addSvc = () => setF({ ...f, services: [...(f.services||[]), { name: '', qty: 1, rate: 0 }] });
  const updSvc = (i, k, v) => { const s = [...f.services]; s[i] = { ...s[i], [k]: v }; setF({ ...f, services: s }); };
  const rmSvc = (i) => setF({ ...f, services: f.services.filter((_, x) => x !== i) });
  const addTr = () => setF({ ...f, travel_lines: [...(f.travel_lines||[]), { label: '', amount: 0 }] });
  const updTr = (i, k, v) => { const t = [...f.travel_lines]; t[i] = { ...t[i], [k]: v }; setF({ ...f, travel_lines: t }); };
  const rmTr = (i) => setF({ ...f, travel_lines: f.travel_lines.filter((_, x) => x !== i) });

  return (
    <div className="fixed inset-0 z-50 flex items-end lg:items-center justify-center bg-black/40 p-0 lg:p-6">
      <div className="w-full max-w-2xl card-cream p-6 lg:rounded-md overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between mb-4">
          <h2 className="serif text-[22px]">{value?.id ? 'Edit Event' : 'Add Event'}</h2>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="eyebrow">Event</label>
              <select value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="event-form-name">
                {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div><label className="eyebrow">Date</label><input type="date" value={f.date} onChange={e => setF({ ...f, date: e.target.value })} data-testid="event-form-date"/></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <AmPmTimePicker value={f.reporting_time} onChange={v => setF({ ...f, reporting_time: v })} label="Artist Reporting Time" testId="event-form-report"/>
            <AmPmTimePicker value={f.ready_by_time} onChange={v => setF({ ...f, ready_by_time: v })} label="Client Ready-by Time" testId="event-form-ready"/>
          </div>
          <div><label className="eyebrow">Venue Name</label><input value={f.venue_name} onChange={e => setF({ ...f, venue_name: e.target.value })} data-testid="event-form-venue"/></div>
          <div><label className="eyebrow">Full Address</label><input value={f.address} onChange={e => setF({ ...f, address: e.target.value })}/></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" style={{width:'auto'}} checked={f.needs_confirmation} onChange={e => setF({ ...f, needs_confirmation: e.target.checked })} data-testid="event-form-needs-conf"/> Needs confirmation</label>
          {f.needs_confirmation && <input placeholder="What needs confirming?" value={f.confirmation_note} onChange={e => setF({ ...f, confirmation_note: e.target.value })}/>}

          {/* Services */}
          <div className="pt-3 border-t hairline">
            <div className="flex items-center justify-between mb-2"><div className="eyebrow">Services</div><button className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" onClick={addSvc}>+ Add</button></div>
            {(f.services||[]).map((s, i) => (
              <div key={i} className="grid grid-cols-[1fr_60px_90px_20px] gap-2 mb-2">
                <input placeholder="Service" value={s.name} onChange={e => updSvc(i,'name',e.target.value)}/>
                <input type="number" placeholder="Qty" value={s.qty} onChange={e => updSvc(i,'qty',parseInt(e.target.value||0))}/>
                <input type="number" placeholder="Rate" value={s.rate} onChange={e => updSvc(i,'rate',parseFloat(e.target.value||0))}/>
                <button onClick={() => rmSvc(i)} className="text-[var(--overdue)]"><X size={14}/></button>
              </div>
            ))}
          </div>

          {/* Travel */}
          <div className="pt-3 border-t hairline">
            <div className="flex items-center justify-between mb-2"><div className="eyebrow">Travel Lines</div><button className="text-xs text-[var(--gold)] tracking-[0.14em] uppercase" onClick={addTr}>+ Add</button></div>
            {(f.travel_lines||[]).map((t, i) => (
              <div key={i} className="grid grid-cols-[1fr_100px_20px] gap-2 mb-2">
                <input placeholder="e.g. Outstation road" value={t.label} onChange={e => updTr(i,'label',e.target.value)}/>
                <input type="number" placeholder="₹" value={t.amount} onChange={e => updTr(i,'amount',parseFloat(e.target.value||0))}/>
                <button onClick={() => rmTr(i)} className="text-[var(--overdue)]"><X size={14}/></button>
              </div>
            ))}
          </div>

          <div><label className="eyebrow">Notes</label><textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })}/></div>
          <button className="btn-gold w-full" onClick={() => onSave(f)} data-testid="event-form-save">Save Event</button>
        </div>
      </div>
    </div>
  );
}
